import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";
import TestPackageForm from "./components/TestPackageForm";
import TestPackageTable from "./components/TestPackageTable";
import API_BASE_URL from "../../services/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error:", error);
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isValidDiscountInput = (s) => {
  const v = String(s || "").trim();
  if (!v) return false;
  if (v.endsWith("%")) {
    const n = Number(v.slice(0, -1).trim());
    return Number.isFinite(n);
  }
  const n = Number(v);
  return Number.isFinite(n);
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ─── Bulk Upload Modal ────────────────────────────────────────────────────────

const BulkUploadModal = ({ onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) validateAndSetFile(dropped);
  };

  const validateAndSetFile = (f) => {
    const allowed = [
      "text/csv",
      "application/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!allowed.includes(f.type) && !f.name.match(/\.(csv|xlsx|xls)$/i)) {
      alert("Only CSV or Excel (.xlsx, .xls) files are accepted.");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) validateAndSetFile(e.target.files[0]);
  };

  const handleDownloadTemplate = async () => {
    try {
      setDownloadingTemplate(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_BASE_URL}/api/tests/bulk-template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      triggerBlobDownload(response.data, "tests_bulk_template.xlsx");
    } catch (e) {
      console.error("Template download error:", e);
      alert("Failed to download template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      setUploading(true);
      setResult(null);
      const token = localStorage.getItem("authToken");
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API_BASE_URL}/api/tests/bulk-upload`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );
      setResult(res.data);
      if (res.data.created > 0) onSuccess?.();
    } catch (e) {
      console.error("Bulk upload error:", e);
      const errData = e?.response?.data;
      if (errData?.issues) {
        setResult({
          created: 0,
          failed: errData.issues.length,
          errors: errData.issues,
        });
      } else {
        alert(errData?.error || "Upload failed. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  };

  const removeFile = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={16} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Bulk Upload Tests
              </h3>
              <p className="text-[11px] text-gray-500">
                Upload CSV or Excel file to create multiple tests
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Step 1 */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-blue-600">1</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-blue-900 mb-0.5">
                  Download Template
                </p>
                <p className="text-[11px] text-blue-700 mb-2">
                  Start with our pre-formatted Excel template. Fill in your test
                  data and upload.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  disabled={downloadingTemplate}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-blue-700 hover:text-blue-900 bg-white border border-blue-200 rounded-md px-3 py-1.5 hover:bg-blue-50 transition-colors disabled:opacity-60">
                  {downloadingTemplate ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Icon name="Download" size={12} />
                      Download Template (.xlsx)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-gray-600">2</span>
              </div>
              <p className="text-xs font-medium text-gray-700">
                Upload Filled File
              </p>
            </div>

            {!file ? (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
                  dragOver
                    ? "border-blue-400 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}>
                <Icon
                  name="FileSpreadsheet"
                  size={28}
                  className={`mx-auto mb-2 ${dragOver ? "text-blue-500" : "text-gray-300"}`}
                />
                <p className="text-xs font-medium text-gray-600 mb-0.5">
                  Drop your file here, or{" "}
                  <span className="text-blue-600 hover:text-blue-700">
                    browse
                  </span>
                </p>
                <p className="text-[11px] text-gray-400">
                  Supports CSV, XLSX, XLS
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-3 flex items-center gap-3">
                <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="FileCheck" size={18} className="text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">
                    {file.name}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={removeFile}
                  className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                  <Icon name="X" size={14} />
                </button>
              </div>
            )}
          </div>

          {/* Result */}
          {result && (
            <div
              className={`rounded-lg p-3 border ${
                result.failed === 0
                  ? "bg-green-50 border-green-200"
                  : result.created > 0 || result.updated > 0
                    ? "bg-amber-50 border-amber-200"
                    : "bg-red-50 border-red-200"
              }`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon
                  name={
                    result.failed === 0
                      ? "CheckCircle"
                      : result.created > 0 || result.updated > 0
                        ? "AlertTriangle"
                        : "XCircle"
                  }
                  size={14}
                  className={
                    result.failed === 0
                      ? "text-green-600"
                      : result.created > 0 || result.updated > 0
                        ? "text-amber-600"
                        : "text-red-600"
                  }
                />
                <p className="text-xs font-semibold text-gray-800">
                  {result.created} created
                  {result.updated > 0 && (
                    <>
                      ,{" "}
                      <span className="text-blue-700">
                        {result.updated} updated
                      </span>
                    </>
                  )}
                  {", "}
                  {result.failed} failed
                </p>
              </div>
              {result.errors?.length > 0 && (
                <div className="max-h-28 overflow-y-auto space-y-0.5">
                  {result.errors.map((err, i) => (
                    <p key={i} className="text-[11px] text-red-700 font-mono">
                      • {err}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-xs px-3 py-1.5">
            {result?.created > 0 ? "Close" : "Cancel"}
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={!file || uploading}
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-1.5 disabled:opacity-60">
            {uploading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                Uploading...
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <Icon name="Upload" size={13} />
                Upload & Import
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const PackagesManagement = () => {
  const { user: currentUser } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [activeTab, setActiveTab] = useState("tests");
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubCategory, setSelectedSubCategory] = useState("");
  const [selectedTestType, setSelectedTestType] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");

  const [bulkDiscountInput, setBulkDiscountInput] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);

  const [tests, setTests] = useState([]);
  const [testNames, setTestNames] = useState([]);
  const [packages, setPackages] = useState([]);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [departments, setDepartments] = useState([]);

  const emptyForm = {
    name: "",
    description: "",
    actualPrice: "",
    offerPrice: "",
    discount: "",
    discountAmount: 0,
    discountInput: "",
    reportWithin: "",
    reportUnit: "hours",
    status: "active",
    sortOrder: 0,
    imageFile: null,
    imgUrl: "",
    removeImage: false,
    testType: "",
    categoryId: "",
    subCategoryId: "",
    otherCategoryIds: [],
    departmentItemId: "",
    numberOfTests: "",
    gender: "",
    sampleRequired: "",
    preparations: "",
    contains: "",
    alsoKnowAs: "",
    spotlight: false,
    features: "",
    selectedTests: [],
    noOfParameter: "",
  };

  const [formData, setFormData] = useState(emptyForm);

  /* ─── API fetchers ─────────────────────────────────────────────────────── */

  const fetchDepartments = async () => {
    try {
      const res = await api.get("/api/departments");
      const list = res.data?.items ?? res.data ?? [];
      setDepartments(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Error fetching departments:", e);
      setDepartments([]);
    }
  };

  const fetchCategories = async () => {
    try {
      if (activeTab === "tests") {
        if (selectedTestType) {
          const response = await api.get("/api/categories/type", {
            params: { type: selectedTestType },
          });
          setCategories(response.data?.categories ?? []);
        } else {
          const response = await api.get("/api/categories");
          setCategories(response.data?.categories ?? response.data ?? []);
        }
      } else {
        const response = await api.get("/api/categories/type", {
          params: { type: "CHECKUP" },
        });
        setCategories(response.data?.categories ?? []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await api.get("/api/subcategories");
      setSubCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      };
      if (activeTab === "tests") {
        if (selectedCategory) params.categoryId = selectedCategory;
        if (selectedSubCategory) params.subCategoryId = selectedSubCategory;
        if (selectedTestType) params.testType = selectedTestType;
        if (selectedDepartment) params.departmentItemId = selectedDepartment;

        params.includeArchived = true; // admins still see archived tests (badge + restore in table)
        const response = await api.get("/api/tests", { params });
        const response1 = await api.get("/api/tests/names", { params });
        setTestNames(response1.data.data || response1.data || []);
        setTests(response.data?.data ?? []);
        setTotalItems(response.data?.total ?? 0);
        setTotalPages(response.data?.totalPages ?? 1);
      } else {
        params.includeArchived = true; // admins still see archived packages (badge + restore in table)
        const response = await api.get("/api/checkups", { params });
        const data = response.data?.data ?? response.data ?? [];
        // /api/checkups returns { data, pagination: { total, totalPages, ... } }
        const pg = response.data?.pagination ?? {};
        const total = pg.total ?? response.data?.total ?? data.length;
        const pages =
          pg.totalPages ??
          response.data?.totalPages ??
          Math.ceil((total ?? 0) / itemsPerPage);
        setTotalPages(Math.max(1, pages));
        setPackages(data);
        setTotalItems(total);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /* ─── Effects ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    fetchSubCategories();
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedTestType]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    currentPage,
    itemsPerPage,
    searchTerm,
    selectedCategory,
    selectedSubCategory,
    selectedTestType,
    selectedDepartment,
  ]);

  const resetFilters = () => {
    setSelectedCategory("");
    setSelectedSubCategory("");
    setSelectedTestType("");
    setSelectedDepartment("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  /* ─── Export ───────────────────────────────────────────────────────────── */

  const handleExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("authToken");

      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (selectedCategory) params.append("categoryId", selectedCategory);
      if (selectedSubCategory)
        params.append("subCategoryId", selectedSubCategory);
      if (selectedTestType) params.append("testType", selectedTestType);
      if (selectedDepartment)
        params.append("departmentItemId", selectedDepartment);

      const response = await axios.get(
        `${API_BASE_URL}/api/tests/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );

      const date = new Date().toISOString().split("T")[0];
      triggerBlobDownload(response.data, `tests_export_${date}.xlsx`);
    } catch (e) {
      console.error("Export error:", e);
      if (e?.response?.status === 404) {
        alert("No tests found for the selected filters.");
      } else {
        alert("Export failed. Please try again.");
      }
    } finally {
      setExporting(false);
    }
  };

  /* ─── Bulk Discount ────────────────────────────────────────────────────── */

  const applyBulkDiscount = async () => {
    try {
      const di = String(bulkDiscountInput || "").trim();
      if (!isValidDiscountInput(di)) {
        alert('Enter valid discount like "10%" or "150"');
        return;
      }
      if (!selectedDepartment && !selectedCategory && !selectedTestType) {
        alert(
          "Select at least one filter: Department or Category or Test Type.",
        );
        return;
      }
      setBulkApplying(true);
      await api.put(
        "/api/tests/bulk-discount",
        {
          discountInput: di,
          departmentItemId: selectedDepartment || undefined,
          categoryId: selectedCategory || undefined,
          testType: selectedTestType || undefined,
        },
        { withCredentials: true },
      );
      setBulkDiscountInput("");
      await fetchData();
      alert("Discount applied to all matching tests.");
    } catch (e) {
      console.error("bulk discount error:", e);
      alert(e?.response?.data?.message || "Failed to apply bulk discount");
    } finally {
      setBulkApplying(false);
    }
  };

  /* ─── Modal handlers ───────────────────────────────────────────────────── */

  const openCreateModal = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setShowAddModal(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    if (activeTab === "tests" && item?.testType) {
      try {
        const response = await api.get("/api/categories/type", {
          params: { type: item.testType },
        });
        setCategories(response.data?.categories ?? []);
      } catch (e) {
        console.error("Failed to fetch categories for edit:", e);
      }
    }
    setFormData({
      name: item.name || "",
      description: item.description || "",
      actualPrice: item.actualPrice ?? "",
      offerPrice: item.offerPrice ?? "",
      discount: item.discount ?? "",
      discountAmount: item.discountAmount ?? 0,
      discountInput:
        item?.discountInput ??
        (item?.discount
          ? `${item.discount}%`
          : item?.discountAmount
            ? String(item.discountAmount)
            : ""),
      reportWithin: item.reportWithin ?? "",
      reportUnit: item.reportUnit || "hours",
      status: item.status || "active",
      sortOrder: item.sortOrder ?? 0,
      imageFile: null,
      imgUrl: item.imgUrl || "",
      removeImage: false,
      testType: item.testType || "",
      categoryId: item.categoryId || "",
      subCategoryId: item.subCategoryId || "",
      otherCategoryIds: (item.otherCategories || []).map((x) =>
        String(x.categoryId),
      ),
      departmentItemId: item.departmentItemId
        ? String(item.departmentItemId)
        : "",
      numberOfTests: item.numberOfTests || "",
      gender: item.gender || "",
      sampleRequired: item.sampleRequired || "",
      preparations: item.preparations || "",
      contains: item.contains || "",
      alsoKnowAs: item.alsoKnowAs || "",
      spotlight: item.spotlight ?? false,
      features: item.features || "",
      selectedTests:
        activeTab !== "tests" ? item?.tests?.map((t) => t.id) || [] : [],
      noOfParameter: item.noOfParameter || "",
    });
    setShowAddModal(true);
  };

  const handleFormTestTypeChange = async (nextType) => {
    setFormData((prev) => ({
      ...prev,
      testType: nextType,
      categoryId: "",
      subCategoryId: "",
      otherCategoryIds: [],
      departmentItemId: "",
    }));
    if (!nextType) {
      setCategories([]);
      return;
    }
    try {
      const response = await api.get("/api/categories/type", {
        params: { type: nextType },
      });
      setCategories(response.data?.categories ?? []);
    } catch (error) {
      console.error("Error fetching categories by testType:", error);
      setCategories([]);
    }
  };

  const handleDelete = async (id) => {
    const isPackage = activeTab !== "tests";
    const confirmMsg = isPackage
      ? "Archive this package?\n\nIt will be hidden from the app and from new orders. Existing bills, bookings and patient records linked to it are NOT affected, and it can be restored later."
      : "Archive this test?\n\nIt will be hidden from the app and from new orders. Existing results, bills and bookings linked to it are NOT affected, and it can be restored later.";
    if (!window.confirm(confirmMsg)) return;
    try {
      const endpoint = isPackage ? `/api/checkups/${id}` : `/api/tests/${id}`;
      const res = await api.delete(endpoint, { withCredentials: true });
      if (res?.data?.message) alert(res.data.message);
      fetchData();
    } catch (error) {
      console.error("Error deleting item:", error);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error deleting item.";
      alert(msg);
    }
  };

  const handleRestore = async (id) => {
    if (
      !window.confirm(
        "Restore this archived item? It will be visible in the app and available for new orders again."
      )
    )
      return;
    try {
      const base = activeTab === "tests" ? "/api/tests" : "/api/checkups";
      await api.post(`${base}/${id}/restore`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error("Error restoring package:", error);
      alert(error?.response?.data?.error || "Error restoring item.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (
        activeTab === "packages" &&
        !editingItem &&
        !formData.imageFile &&
        !formData.imgUrl
      ) {
        alert("Image is required for Health Checkup (Package).");
        setSaving(false);
        return;
      }
      const fd = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "imageFile") return;
        const value = formData[key];
        if (key === "selectedTests" && Array.isArray(value)) {
          value.forEach((v) => fd.append("selectedTests[]", v));
          return;
        }
        if (key === "otherCategoryIds" && Array.isArray(value)) {
          value.forEach((v) => fd.append("otherCategoryIds[]", v));
          return;
        }
        if (key === "removeImage") {
          fd.append("removeImage", value ? "true" : "false");
          return;
        }
        if (value !== null && value !== undefined && value !== "")
          fd.append(key, value);
      });
      if (activeTab === "packages" && formData.imageFile)
        fd.append("image", formData.imageFile);
      const endpoint = activeTab === "tests" ? "/api/tests" : "/api/checkups";
      if (editingItem) {
        await api.put(`${endpoint}/${editingItem.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
      } else {
        await api.post(endpoint, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });
      }
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      console.error("Error saving item:", error);
      alert(
        error.response?.data?.message || "Error saving item. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const departmentOptions = useMemo(() => {
    return (departments || [])
      .slice()
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );
  }, [departments]);

  const hasActiveFilters = !!(
    selectedCategory ||
    selectedSubCategory ||
    selectedTestType ||
    selectedDepartment ||
    searchTerm
  );

  /* ─── Render ───────────────────────────────────────────────────────────── */

  return (
    <div className="min-h-screen bg-gray-50">
      <Header isMenuOpen={sidebarOpen} />
     <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"}`}>
        <div className="p-4">
          <Breadcrumb />

          {/* ── Page Header ── */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-1">
                Health Tests Management
              </h1>
              <p className="text-xs text-gray-600">
                Manage tests and health packages
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {activeTab === "tests" && (
                <>
                  {/* Export */}
                  <Button
                    onClick={handleExport}
                    disabled={exporting || !canAccess(currentUser, "canDownload")}
                    className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-3 py-2 flex items-center gap-1.5 disabled:opacity-60">
                    {exporting ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Icon
                          name="Download"
                          size={14}
                          className="text-emerald-600"
                        />
                        <span>
                          Export{hasActiveFilters ? " Filtered" : " All"}
                        </span>
                      </>
                    )}
                  </Button>

                  {/* Bulk Upload */}
                  <Button
                    onClick={() => setShowBulkUploadModal(true)}
                    disabled={!canAccess(currentUser, "canCreate")}
                    className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs px-3 py-2 flex items-center gap-1.5">
                    <Icon
                      name="FileSpreadsheet"
                      size={14}
                      className="text-green-600"
                    />
                    Bulk Upload
                  </Button>
                </>
              )}

              <Button
                onClick={openCreateModal}
                disabled={!canAccess(currentUser, "canCreate")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 flex items-center gap-1">
                <Icon name="Plus" size={14} />
                Add {activeTab === "tests" ? "Test" : "Package"}
              </Button>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="mb-4">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-4">
                {[
                  { key: "tests", label: "Individual Tests" },
                  { key: "packages", label: "Health Packages" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => {
                      setActiveTab(key);
                      resetFilters();
                    }}
                    className={`py-2 px-1 border-b-2 font-medium text-xs ${
                      activeTab === key
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}>
                    {label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm mb-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              {/* Search */}
              <div className="md:col-span-4">
                <label className="block text-[11px] font-medium text-gray-700 mb-1">
                  Search {activeTab === "tests" ? "Tests" : "Packages"}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <Icon name="Search" size={14} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder={`Search by name, description${activeTab === "tests" ? " or test type" : ""}...`}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="block w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs h-9"
                  />
                </div>
              </div>

              {activeTab === "tests" && (
                <>
                  {/* Department */}
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Department
                    </label>
                    <select
                      value={selectedDepartment}
                      onChange={(e) => {
                        setSelectedDepartment(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9">
                      <option value="">All Departments</option>
                      {departmentOptions.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Test Type */}
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Test Type
                    </label>
                    <select
                      value={selectedTestType}
                      onChange={(e) => {
                        setSelectedTestType(e.target.value);
                        setSelectedCategory("");
                        setSelectedSubCategory("");
                        setCurrentPage(1);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9">
                      <option value="">All Types</option>
                      <option value="PATHOLOGY">Pathology</option>
                      <option value="RADIOLOGY">Radiology</option>
                      <option value="VITAL_ORGAN">Vital Organ</option>
                      <option value="PROFILE">Health Profile</option>
                    </select>
                  </div>

                  {/* Category */}
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedSubCategory("");
                        setCurrentPage(1);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9">
                      <option value="">All Categories</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sub Category */}
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-medium text-gray-700 mb-1">
                      Sub Category
                    </label>
                    <select
                      value={selectedSubCategory}
                      onChange={(e) => {
                        setSelectedSubCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9">
                      <option value="">All Sub Categories</option>
                      {subCategories.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Active filter pills + export hint */}
            {activeTab === "tests" && hasActiveFilters && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-gray-400 font-medium">
                  Filters:
                </span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm("")}
                      className="hover:text-red-500">
                      <Icon name="X" size={9} />
                    </button>
                  </span>
                )}
                {selectedTestType && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                    {selectedTestType}
                    <button
                      onClick={() => setSelectedTestType("")}
                      className="hover:text-red-500">
                      <Icon name="X" size={9} />
                    </button>
                  </span>
                )}
                {selectedDepartment && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full">
                    {departmentOptions.find(
                      (d) => String(d.id) === selectedDepartment,
                    )?.name ?? selectedDepartment}
                    <button
                      onClick={() => setSelectedDepartment("")}
                      className="hover:text-red-500">
                      <Icon name="X" size={9} />
                    </button>
                  </span>
                )}
                {selectedCategory && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full">
                    {categories.find((c) => String(c.id) === selectedCategory)
                      ?.name ?? selectedCategory}
                    <button
                      onClick={() => setSelectedCategory("")}
                      className="hover:text-red-500">
                      <Icon name="X" size={9} />
                    </button>
                  </span>
                )}
                {selectedSubCategory && (
                  <span className="inline-flex items-center gap-1 text-[10px] bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full">
                    {subCategories.find(
                      (s) => String(s.id) === selectedSubCategory,
                    )?.name ?? selectedSubCategory}
                    <button
                      onClick={() => setSelectedSubCategory("")}
                      className="hover:text-red-500">
                      <Icon name="X" size={9} />
                    </button>
                  </span>
                )}
                <span className="text-[10px] text-gray-400 italic ml-1">
                  — Export will include all {totalItems} matching records
                </span>
              </div>
            )}

            {/* Bulk Discount */}
            {activeTab === "tests" && (
              <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50 p-3">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-10">
                    <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                      Apply Discount to All Filtered Tests
                    </label>
                    <input
                      value={bulkDiscountInput}
                      onChange={(e) => setBulkDiscountInput(e.target.value)}
                      placeholder="e.g. 10% or 150"
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 h-9"
                    />
                    <p className="text-[10px] text-gray-500 mt-1 leading-tight">
                      Select Department or Category or Test Type, then click
                      Apply once. It updates all matching tests in DB.
                    </p>
                  </div>
                  <div className="md:col-span-2 flex md:justify-end">
                    <Button
                      type="button"
                      onClick={applyBulkDiscount}
                      disabled={bulkApplying}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-4 py-2 h-9 disabled:opacity-60 w-full md:w-auto">
                      {bulkApplying ? "Applying..." : "Apply to All"}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Reset */}
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={resetFilters}
                className="text-[11px] font-semibold text-gray-600 hover:text-gray-900">
                Reset filters
              </button>
            </div>
          </div>

          <TestPackageTable
            data={activeTab === "tests" ? tests : packages}
            loading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onRestore={handleRestore}
            type={activeTab === "tests" ? "test" : "package"}
            searchTerm={searchTerm}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => fetchData()}
        />
      )}

      {/* Add / Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                {editingItem ? "Edit" : "Add New"}{" "}
                {activeTab === "tests" ? "Test" : "Package"}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600">
                <Icon name="X" size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-4 py-3 space-y-4 max-h-[60vh] overflow-y-auto">
                <TestPackageForm
                  formData={formData}
                  onChange={setFormData}
                  onTestTypeChange={handleFormTestTypeChange}
                  type={activeTab === "tests" ? "test" : "package"}
                  categories={categories}
                  subCategories={subCategories}
                  tests={activeTab === "packages" ? tests : []}
                  testNames={activeTab === "packages" ? testNames : []}
                  departments={departments}
                />
              </div>

              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddModal(false)}
                  className="text-xs px-3 py-1.5">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  size="sm"
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 disabled:opacity-60">
                  {saving ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                      Saving...
                    </div>
                  ) : (
                    <>
                      {editingItem ? "Update" : "Create"}{" "}
                      {activeTab === "tests" ? "Test" : "Package"}
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackagesManagement;
